import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';

/**
 * CookieConsent Component
 * GDPR Compliance - Cookie consent banner for EU users
 * 
 * Manages consent for:
 * - Essential cookies (always enabled - Firebase Auth)
 * - Analytics cookies (Google Analytics - requires consent)
 * 
 * Complies with:
 * - GDPR (General Data Protection Regulation)
 * - ePrivacy Directive
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookie_consent');
    const consentDate = localStorage.getItem('cookie_consent_date');
    
    // Show banner if:
    // 1. No consent stored, OR
    // 2. Consent is older than 12 months (GDPR recommends re-asking annually)
    if (!consent) {
      setShowBanner(true);
    } else if (consentDate) {
      const twelveMonthsAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
      if (parseInt(consentDate) < twelveMonthsAgo) {
        setShowBanner(true);
      }
    }

    // Initialize analytics based on stored consent
    if (consent === 'accepted') {
      initializeAnalytics();
    }
  }, []);

  const initializeAnalytics = () => {
    // Initialize Google Analytics if consent given
    if (window.gtag && process.env.REACT_APP_GA4_MEASUREMENT_ID) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
      logger.info('CookieConsent', 'Info');
    }
  };

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_consent_date', Date.now().toString());
    localStorage.setItem('analytics_consent', 'true');
    
    initializeAnalytics();
    setShowBanner(false);
    
    // Track consent (non-personally identifiable)
    if (window.gtag) {
      window.gtag('event', 'cookie_consent', {
        'event_category': 'consent',
        'event_label': 'accepted'
      });
    }
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    localStorage.setItem('cookie_consent_date', Date.now().toString());
    localStorage.setItem('analytics_consent', 'false');
    
    // Ensure analytics is NOT initialized
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    
    setShowBanner(false);
    logger.info('CookieConsent', 'Info');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t-4 border-blue-500 shadow-2xl z-[99999] animate-slideUp">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Main Content */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Text Content */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">🍪</span>
              <h3 className="text-xl font-bold text-gray-900">
                We value your privacy
              </h3>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              We use cookies to improve your experience and analyze site usage. 
              Essential cookies are required for the site to function. 
              {!showDetails && (
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-blue-600 underline hover:text-blue-700 ml-1"
                >
                  Learn more
                </button>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={handleRejectNonEssential}
              className="px-6 py-3 text-sm font-semibold bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Reject Non-Essential
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-3 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Detailed Information (expandable) */}
        {showDetails && (
          <div className="mt-6 pt-6 border-t border-gray-200 animate-fadeIn">
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              {/* Essential Cookies */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span>✅</span> Essential Cookies
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    Always Active
                  </span>
                </h4>
                <p className="text-gray-600 mb-2">
                  Required for the website to function properly. These cannot be disabled.
                </p>
                <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
                  <li>Firebase Authentication (login sessions)</li>
                  <li>LocalStorage (app preferences, offline functionality)</li>
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span>📊</span> Analytics Cookies
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    Optional
                  </span>
                </h4>
                <p className="text-gray-600 mb-2">
                  Help us understand how you use the site so we can improve it.
                </p>
                <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
                  <li>Google Analytics (anonymous usage tracking)</li>
                  <li>Vercel Analytics (performance monitoring)</li>
                </ul>
              </div>
            </div>

            {/* Privacy Links */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                For more information, read our{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-700"
                >
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a 
                  href="/terms-of-service" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-700"
                >
                  Terms of Service
                </a>
                . You can change your cookie preferences at any time in your account settings.
              </p>
            </div>

            <button
              onClick={() => setShowDetails(false)}
              className="mt-3 text-xs text-blue-600 underline hover:text-blue-700"
            >
              Show less
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;

